//TODO vibe coded

'use client';

import { useState, useEffect, useMemo, useRef, FormEvent, ReactNode, type CSSProperties, type RefObject } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { Appearance, StripeElementsOptions } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { LightDomSlot } from '../../shared/LightDomSlot';

interface ProPaymentStripeFormProps {
  clientSecret: string;
  stripePublishableKey: string;
  stripeConnectAccountId?: string;
  payLabel: string;
  errorLabel: string;
  /** When true, the pay button is disabled (e.g. cancellation policy not accepted) */
  disabled?: boolean;
  /** External loading state (e.g. the "pay on site" button is in progress) — shows the loading indicator and disables the button */
  busy?: boolean;
  /** Rendered between the payment element and the pay button (e.g. cancellation policy checkbox) */
  extraContent?: ReactNode;
  /**
   * Stripe `return_url` for redirect-based methods (Klarna…), built with `buildRedirectReturnUrl`.
   * Required: the page it points to must resume the tunnel (`useRedirectReturn`), otherwise the
   * customer comes back to a reset form with no outcome.
   */
  returnUrl: string | undefined;
  onSuccess: () => void;
  onError?: (error: string) => void;
  /** Notifies the parent when the Stripe confirmation is processing */
  onProcessingChange?: (processing: boolean) => void;
}

/* Apparence historique : thème clair de Stripe. Reste le défaut — tout ce qui suit ne fait
   que basculer en thème sombre quand le tunnel hôte l'est. */
const LIGHT_APPEARANCE: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#0066cc',
    colorDanger: '#df1b41',
  },
};

const DARK_APPEARANCE: Appearance = {
  ...LIGHT_APPEARANCE,
  theme: 'night',
};

/** Croisement noir/blanc de WCAG 2.x : en dessous, du texte blanc contraste mieux que du noir. */
const DARK_SURFACE_LUMINANCE = 0.1791;

/* La sonde ne sert qu'à faire résoudre `--bt-bg` par le navigateur ; `transparent` est le
   marqueur « pas de polarité lisible ici » (cf. useTunnelStripeAppearance).

   ⚠️ `background-color` et NON `border-color`, alors que les deux résoudraient le token. La
   valeur du fond n'est pas validée (`?bg=white` devient le token invalide `#white`, cf.
   normalizeColor dans frontend-web/src/lib/tunnel-theme.ts). Une substitution invalide rend la
   propriété invalid-at-computed-value-time, qui reprend alors sa valeur INITIALE : `transparent`
   pour `background-color`, mais `currentColor` pour `border-color` — c'est-à-dire l'encre du
   tunnel, sombre. Avec `border-color`, le garde-fou s'inversait donc exactement dans le cas
   qu'il doit couvrir : Stripe basculait en thème sombre sur un tunnel clair. */
const THEME_PROBE_STYLE: CSSProperties = {
  display: 'none',
  color: 'transparent',
  backgroundColor: 'var(--bt-bg, transparent)',
};

/**
 * Luminance relative WCAG d'un `rgb()` / `rgba()` sérialisé par le navigateur.
 * Renvoie `undefined` si la couleur n'est pas opaque — c'est le cas quand le token `--bt-*`
 * n'existe pas dans le contexte (repli `transparent`), et on ne veut alors surtout pas décider
 * d'une polarité au hasard.
 */
function computedColorLuminance(value: string): number | undefined {
  /* On ne parse QUE la sérialisation sRGB. Un thème hôte peut poser `--color-bg` dans un espace
     moderne (`oklch(…)`, `color(display-p3 …)`), que le navigateur sérialise tel quel : leurs
     nombres n'ont alors rien à voir avec des canaux 0-255 et une regex naïve en déduirait une
     polarité fausse. Tout ce qu'on ne sait pas lire retombe sur le thème clair. */
  if (!/^rgba?\(/.test(value)) return undefined;
  const parts = value.match(/[\d.]+/g);
  if (!parts || parts.length < 3) return undefined;
  const alpha = parts.length > 3 ? Number(parts[3]) : 1;
  if (alpha < 1) return undefined;
  const [r, g, b] = parts.slice(0, 3).map((raw): number => {
    const v = Number(raw) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Aligne l'apparence du PaymentElement sur la polarité du tunnel hôte.
 *
 * Le PaymentElement vit dans une iframe cross-origin : les tokens `--bt-*` n'y existent pas, il
 * faut donc lui passer des valeurs déjà résolues. On monte une sonde masquée qui porte `--bt-bg`
 * en `background-color` — que le navigateur normalise en `rgb(…)` même sur un élément
 * `display: none` — et on lit sa luminance.
 *
 * Sans ça, Stripe reste en thème clair et dessine ses libellés (« Numéro de carte », « Date
 * d'expiration », « CVC ») en gris foncé : sur un tunnel à fond sombre ils disparaissent, et le
 * client ne voit plus que trois rectangles blancs flottants.
 *
 * Fail-safe : hors tunnel (ProPaymentTunnel) ou si la lecture échoue, on garde LIGHT_APPEARANCE,
 * c'est-à-dire exactement le rendu d'avant.
 */
function useTunnelStripeAppearance(): [RefObject<HTMLSpanElement | null>, Appearance] {
  const probeRef = useRef<HTMLSpanElement>(null);
  const [appearance, setAppearance] = useState<Appearance>(LIGHT_APPEARANCE);

  useEffect((): void => {
    const probe = probeRef.current;
    if (!probe) return;
    const luminance = computedColorLuminance(getComputedStyle(probe).backgroundColor);
    if (luminance === undefined) return;
    setAppearance(luminance < DARK_SURFACE_LUMINANCE ? DARK_APPEARANCE : LIGHT_APPEARANCE);
  }, []);

  return [probeRef, appearance];
}

function CheckoutForm({
  clientSecret,
  payLabel,
  errorLabel,
  disabled,
  busy,
  extraContent,
  returnUrl,
  onSuccess,
  onError,
  onProcessingChange,
}: {
  clientSecret: string;
  payLabel: string;
  errorLabel: string;
  disabled?: boolean;
  busy?: boolean;
  extraContent?: ReactNode;
  returnUrl: string | undefined;
  onSuccess: () => void;
  onError?: (error: string) => void;
  onProcessingChange?: (processing: boolean) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // CARD_SAVE : le backend renvoie un SetupIntent (client_secret `seti_…`) — enregistrement de la
  // carte sans débit ni blocage. La confirmation passe alors par confirmSetup, pas confirmPayment.
  const isSetupIntent = clientSecret.startsWith('seti_');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    onProcessingChange?.(true);
    setErrorMessage(null);

    try {
      if (isSetupIntent) {
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: returnUrl ? { return_url: returnUrl } : undefined,
          redirect: 'if_required',
        });
        if (error) {
          setErrorMessage(error.message || errorLabel);
          onError?.(error.message || errorLabel);
        } else if (setupIntent?.status === 'succeeded') {
          onSuccess();
        }
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: returnUrl ? { return_url: returnUrl } : undefined,
        redirect: 'if_required',
      });
      if (error) {
        setErrorMessage(error.message || errorLabel);
        onError?.(error.message || errorLabel);
      } else if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'requires_capture') {
        onSuccess();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : errorLabel;
      setErrorMessage(message);
      onError?.(message);
    } finally {
      setIsProcessing(false);
      onProcessingChange?.(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="pp-stripe-form">
      {/* Widgets (Shadow DOM) : Stripe ne monte pas dans un shadow root → seul le PaymentElement est
          projeté dans le Light DOM de l'hôte ; bouton, erreurs et extraContent restent dans le shadow,
          stylés par la feuille du widget et hors de portée du CSS du site hôte. Sans effet ailleurs. */}
      <LightDomSlot>
        <PaymentElement options={{ layout: 'tabs', wallets: { link: 'never' } }} />
      </LightDomSlot>
      {errorMessage && <div className="pp-error-message">{errorMessage}</div>}
      {extraContent}
      <button type="submit" className="pp-pay-button" disabled={!stripe || isProcessing || disabled || busy}>
        <svg
          className="pp-pay-button-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {isProcessing || busy ? '...' : payLabel}
      </button>
    </form>
  );
}

const stripeCache = new Map<string, ReturnType<typeof loadStripe>>();

// Disable Stripe's fraud-signal beacon (GDPR / no third-party tracking).
// Must be called before any `loadStripe()` invocation.
loadStripe.setLoadParameters({ advancedFraudSignals: false });

function getStripePromise(publishableKey: string, connectAccountId?: string): ReturnType<typeof loadStripe> {
  const cacheKey = `${publishableKey}__${connectAccountId ?? ''}`;
  const cached = stripeCache.get(cacheKey);
  if (cached) return cached;
  const promise = loadStripe(publishableKey, connectAccountId ? { stripeAccount: connectAccountId } : undefined);
  stripeCache.set(cacheKey, promise);
  return promise;
}

export function ProPaymentStripeForm({
  clientSecret,
  stripePublishableKey,
  stripeConnectAccountId,
  payLabel,
  errorLabel,
  disabled,
  busy,
  extraContent,
  returnUrl,
  onSuccess,
  onError,
  onProcessingChange,
}: ProPaymentStripeFormProps) {
  const stripePromise = getStripePromise(stripePublishableKey, stripeConnectAccountId);
  const [themeProbeRef, appearance] = useTunnelStripeAppearance();

  /* Mémoïsé : `Elements` répercute tout changement d'`options` via `elements.update()`, et une
     nouvelle référence à chaque rendu déclencherait cette mise à jour pour rien. */
  const options = useMemo(
    (): StripeElementsOptions => ({ clientSecret, appearance, loader: 'auto' }),
    [clientSecret, appearance],
  );

  return (
    <>
      <span ref={themeProbeRef} aria-hidden="true" style={THEME_PROBE_STYLE} />
      <Elements stripe={stripePromise} options={options}>
        <CheckoutForm
          clientSecret={clientSecret}
          payLabel={payLabel}
          errorLabel={errorLabel}
          disabled={disabled}
          busy={busy}
          extraContent={extraContent}
          returnUrl={returnUrl}
          onSuccess={onSuccess}
          onError={onError}
          onProcessingChange={onProcessingChange}
        />
      </Elements>
    </>
  );
}
